import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { GQL_ID } from '@@partner:types:graphql'
import React, { useCallback, useEffect, useState } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { makeDefaultState, mapStateToApiVariable, mapApiDataToState } from './ShopTagFormState'
import { useFetchSingleShopTag } from '@@partner:graphql/query/shopTag/singleShopTag.gql'
import { useMutateUpdateShopTagInfo } from '@@partner:graphql/mutation/shopTag/updateShopTagInfo.gql'
import { makeGraphQLErrorNotification, notifyError, notifyOk } from '@@partner:helpers/notification'
import { ROUTE_SHOP_TAGS_EDIT } from '@@partner:config/routes'
import { commonFieldChange } from '@@partner:helpers/util'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import { Col, Form, Grid, Panel, Row } from 'rsuite'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'
import { ShopTagTranslation } from '@@partner:types:app'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import { API_URL_UPLOAD_SHOP_TAG_ICON } from '@@partner:config/api'
import { UPLOAD_NAME_SHOP_TAG_ICON } from '@@partner:config/shop'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import DropzonePreview from '@components/common/form/DropzonePreview'
import { SaveButton } from '@components/common/form/Buttons'

type ShopTagFormProps = {
  shopTagId?: GQL_ID
  brandSlug: string
}

export default function ShopTagForm ({
  shopTagId,
  brandSlug
}: ShopTagFormProps) {
  const history = useHistory()

  const [ selectedLanguage, setSelectedLanguage ] = useState<string>()
    
  const { formState, setFormState, setFormRef, setFormErrors } =
    useValidatableForm(() => makeDefaultState(shopTagId))
  

  const { shopTag, loading, error } = useFetchSingleShopTag({
    shopTagId
  })

  const [
    updateShopTagInfo,
    { loading: loadingUpdateShopInfo, error: errorUpdatingShopInfo }
  ] = useMutateUpdateShopTagInfo()

  // set initial from backend
  useEffect(() => {
    if (!loading && shopTag) {
      setFormState((state) => mergeLeft(state, mapApiDataToState(shopTag)))
    }
  }, [ shopTag, loading, setFormState ])

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        updateShopTagInfo({
          variables: {
            newData: mapStateToApiVariable(formState),
            upsert: shopTagId ? false : true
          }
        })
          .then(({ data }) => {
            if (shopTagId) {
              notifyOk('Тег обновлён')
              if (data?.shopTagInfo) {
                setFormState((state) => mergeLeft(state, mapApiDataToState(data?.shopTagInfo)))
              }
            }
            else if (data?.shopTagInfo) {
              notifyOk('Тег создан')
              const url = generatePath(ROUTE_SHOP_TAGS_EDIT, {
                brandSlug,
                shopTagId: data?.shopTagInfo.id
              })
              history.replace(url)
            }
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, setFormState, updateShopTagInfo, shopTagId, brandSlug, history ]
  )

  const handleFieldChange = commonFieldChange(setFormState)

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleTranslateChange = useCallback(
    (field: keyof ShopTagTranslation) => (value: string) => {
      if (!selectedLanguage) {
        return
      }
      const newTranslations = [ ...formState.i18n ]
      const translationIndex = newTranslations.findIndex(translation => translation.language_code === selectedLanguage)
      if (translationIndex > -1) {
        newTranslations[translationIndex][field] = value
      }
      else {
        const newTranslation = mergeLeft({
          language_code: selectedLanguage,
          name: ''
        }, {
          language_code: selectedLanguage,
          [field]: value
        })
        newTranslations.push(newTranslation)
      }
      setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
    },
    [ formState.i18n, selectedLanguage, setFormState ]
  )

  const handleUploadIcon = useCallback(() => (
    icon_id: GQL_ID,
    url: string
  ) => {
    setFormState((state) => {
      return {
        ...state,
        icon: {
          url
        },
        icon_id
      }
    })
  }, [ setFormState ])

  const handleUnlinkIcon = useCallback(() => () => {
    setFormState((state) =>
      ({
        ...state,
        icon: {
          url: undefined
        },
        icon_id: undefined 
      })
    )
  }, [ setFormState ])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить данные сервиса #${shopTagId} для редактирования`}
        error={error}
      />
    )
  }

  if (loading) {
    return (
      <AppLoader.RowsWithText content="Загружаем тег" />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      className="ShopForm"
    >
      { loading && <AppLoader.Inner content="Загрузка..." /> }

      { !!errorUpdatingShopInfo && (
        <GraphQLCommonErrorDisplay
          title={'Не удалось сохранить данные тега'}
          error={errorUpdatingShopInfo}
        />
      )}
      <Grid fluid>
        <Row>
          <Col md={14}>
            <Panel
              shaded
              header='Данные сервиса'        
              className='my-4'
            >
              <Row className='my-4'>
                <Col md={12}>
                  <Form.Group controlId='system_name'>
                    <Form.ControlLabel>Техническое название</Form.ControlLabel>
                    <Form.Control
                      name='system_name'
                      value={formState.system_name}
                      onChange={handleFieldChange}
                      disabled={loadingUpdateShopInfo}
                    />
                  </Form.Group>
  
                  <Form.Group>
                    <Form.ControlLabel>
                      Вес (чем больше - тем выше сервис)
                    </Form.ControlLabel>
                    <Form.Control placeholder="Вес" name="sort_order" />
                  </Form.Group>
                </Col>
              </Row>
              <Panel 
                shaded
                header='Перевод'
                className='my-4'
              >
                <Row className='my-4'>
                  <Form.Group>
                    <Form.ControlLabel>По-умолчанию</Form.ControlLabel>
                    <Form.Control placeholder="Название" name="name" />
                  </Form.Group>
                  <Form.ControlLabel>Перевод</Form.ControlLabel>
                  <SelectLocale
                    selectedLanguage={selectedLanguage}
                    onSelect={(language) => setSelectedLanguage(language)}
                    onAdd={(language) => {
                      setFormState((state) => mergeLeft(state, { languages: [ ...state.languages, language ] }))
                      setSelectedLanguage(language)
                    }}
                    languages={formState.languages}
                  />
                  { selectedLanguage && (
                    <LocalizedForm
                      name='name'
                      label='Название'
                      selectedLanguage={selectedLanguage}
                      value={formState.i18n?.find((translation) => translation.language_code === selectedLanguage)?.name || ''}
                      onChange={handleTranslateChange('name')}
                    />
                  )}
                </Row>
              </Panel>
            </Panel>
          </Col>
          <Col xs={24} md={10}>
            <Row gutter={6} className="mt-4">
              <Col sm={formState.icon?.url ? 18 : 24}>
                <Form.Group>
                  <Form.ControlLabel>Иконка</Form.ControlLabel>
                  <DropzoneUploader
                    action={API_URL_UPLOAD_SHOP_TAG_ICON}
                    name={UPLOAD_NAME_SHOP_TAG_ICON}
                    onUploaded={handleUploadIcon()}
                    multiple={false}
                  />
                </Form.Group>
              </Col>
  
              {formState.icon?.url && (
                <Col sm={6}>
                  <DropzonePreview
                    url={formState.icon.url}
                    onDelete={handleUnlinkIcon()}
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Grid>

      <SaveButton
        loading={loading || loadingUpdateShopInfo}
        disabled={loading || loadingUpdateShopInfo}
      >
        Cохранить
      </SaveButton>
    </Form>
    

  )
}